import {
  CustomAttribute,
  CustomAttributeValue,
  isSelectAttribute,
} from '../../sdk/types/common/customAttribute';

export type CustomAttributeOption = {
  value: string;
  label: string;
};

/**
 * Public — re-exported from the `HeadlessComponents` barrel. Use this in
 * any consumer that builds its own custom-attribute UI on top of the shared
 * Layer A inputs (e.g. inline-edit pickers, batch-edit forms) and needs to
 * read the current value(s) for one attribute out of an issue's
 * `CustomAttributeValue[]` array.
 *
 * Filters out `null`/`undefined` so consumers can render the values directly.
 * Returns `[]` when the attribute is missing — keeps render branches simple
 * (`values[0] ?? ''` etc.).
 */
export const getValueForAttribute = (
  customAttributes: CustomAttributeValue[] | undefined,
  attributeId: string,
): string[] => {
  const entry = customAttributes?.find((v) => v.id === attributeId);
  return entry?.values?.filter((v): v is string => v != null) ?? [];
};

/**
 * Public — re-exported from the `HeadlessComponents` barrel. The companion
 * to `getValueForAttribute`: produces the next `CustomAttributeValue[]` for an
 * issue when one attribute's values change. Use it in any consumer that drives
 * the shared inputs (Formik form, inline edit, batch edit, etc.) so the
 * canonical "no value" shape is consistent across surfaces.
 *
 * Updates one attribute's values while preserving every other attribute's
 * entry. When the new values are empty (`[]`, `['']`, or `[null]`), the entry
 * is removed entirely so the resulting array is the canonical "no value"
 * shape — i.e. an attribute with no values does not appear in the array at
 * all, rather than appearing with an empty `values: []`.
 */
export const updateAttribute = (
  current: CustomAttributeValue[] | undefined,
  attributeId: string,
  newValues: Array<string | null>,
): CustomAttributeValue[] => {
  const filtered = (current ?? []).filter((a) => a.id !== attributeId);
  if (newValues.length === 0 || (newValues.length === 1 && !newValues[0])) {
    return filtered;
  }
  return [...filtered, { id: attributeId, values: newValues }];
};

/**
 * Public — re-exported from the `HeadlessComponents` barrel. Used
 * internally by `SelectAttributeInput` / `MultiSelectAttributeInput` and
 * available to consumers that need to render the same option list elsewhere
 * (e.g. a confirmation dialog, a read-only summary).
 *
 * Maps `attribute.values` (the API enum value list) to dropdown option
 * format. Returns `[]` for non-select attributes so callers don't have to
 * branch.
 */
export const mapCustomAttributeOptions = (attribute: CustomAttribute): CustomAttributeOption[] => {
  if (!isSelectAttribute(attribute)) return [];
  return attribute.values.map((v) => ({
    value: v.id,
    label: v.name,
  }));
};
